import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Upload, RefreshCw, Store, MessageCircle, Bell, Building2, ArrowRight, FileText, ShoppingBag, PillBottle } from 'lucide-react';

import { Card, CardContent } from '../../components/ui/card';
import { usePortalAuth } from '../PortalAuthContext';
import { usePortalDestination } from '../PortalDestinationContext';
import { PortalRefillNudges } from './PortalRefillNudges';
import { PortalEmptyState } from '../ui/PortalEmptyState';
import { PortalSkeletonRows } from '../ui/PortalSkeletonRows';

const QUICK_ACTIONS = [
  { page: 'refills', icon: RefreshCw, label: 'Request refill' },
  { page: 'prescriptions', search: '?upload=1', icon: Upload, label: 'Upload Rx' },
  { page: 'shop', icon: Store, label: 'Shop OTC' },
  { page: 'chat', icon: MessageCircle, label: 'Chat' },
];

const RX_STATUS_LABEL = { pending: 'Awaiting review', reviewed: 'Approved', rejected: 'Not accepted' };
const REFILL_STATUS_LABEL = { pending: 'Awaiting pharmacy', approved: 'Approved', ready: 'Ready for pickup', collected: 'Collected', rejected: 'Not approved' };

export default function PortalDashboardPage() {
  const { patient } = usePortalAuth();
  const { api: portalApi, href, branch, openPicker } = usePortalDestination();
  const [stats, setStats] = useState({ rx: 0, refills: 0, orders: 0, reminders: 0 });
  const [activity, setActivity] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [loadingFeed, setLoadingFeed] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoadingFeed(true);
      try {
        const [rx, ref, ord, rem] = await Promise.all([
          portalApi.get('/prescriptions'),
          portalApi.get('/refills'),
          portalApi.get('/orders'),
          portalApi.get('/reminders'),
        ]);
        if (!live) return;
        const rxRows = rx.data.prescriptions || [];
        const refillRows = ref.data.refills || [];
        const orderRows = ord.data.orders || [];
        const reminderRows = rem.data.reminders || [];

        setStats({
          rx: rxRows.length,
          refills: refillRows.filter(r => ['pending', 'approved', 'ready'].includes(r.status)).length,
          orders: orderRows.length,
          reminders: reminderRows.filter(r => r.active !== false).length,
        });

        const feed = [
          ...rxRows.map(r => ({ key: `rx-${r.rx_id}`, icon: FileText, title: r.original_filename || 'Prescription uploaded',
            subtitle: RX_STATUS_LABEL[r.status] || 'Awaiting review', date: r.created_at, to: href('prescriptions') })),
          ...refillRows.map(r => ({ key: `refill-${r.request_id}`, icon: PillBottle,
            title: (r.items || []).map(i => i.product_name).join(', ') || 'Refill requested',
            subtitle: REFILL_STATUS_LABEL[r.status] || 'Awaiting pharmacy', date: r.created_at, to: href('refills') })),
          ...orderRows.map(o => ({ key: `order-${o.sale_id}`, icon: ShoppingBag,
            title: (o.items || []).slice(0, 2).map(i => i.product_name).join(', ') || 'Order placed',
            subtitle: `GHS ${Number(o.total || 0).toFixed(2)}`, date: o.created_at, to: href('orders') })),
        ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 4);
        setActivity(feed);
        setUpcomingReminders(reminderRows.filter(r => r.active !== false).slice(0, 3));
      } catch { /* dashboard is best-effort */ }
      finally { if (live) setLoadingFeed(false); }
    })();
    return () => { live = false; };
  }, [portalApi, href]);

  const noBranch = !branch;

  return (
    <div className="space-y-6 stagger-children" data-testid="portal-dashboard">
      <section className="portal-welcome rounded-2xl p-6">
        <p className="portal-welcome-sub text-sm">Good to see you,</p>
        <h1 className="text-3xl font-bold" data-testid="portal-patient-greeting">{patient?.name?.split(' ')[0] || 'friend'}</h1>
        <p className="portal-welcome-sub text-sm mt-2 max-w-md">
          Your personal pharmacy — refills, prescriptions and health guidance in one place.
        </p>
        {noBranch && (
          <button type="button" onClick={openPicker} data-testid="portal-choose-pharmacy-cta"
            className="portal-welcome-cta mt-4 inline-flex items-center gap-2 text-sm font-medium rounded-lg px-3 py-2.5">
            <Building2 className="w-4 h-4" aria-hidden="true" /> Choose a pharmacy <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        )}
      </section>

      {/* One highest-priority actionable card. */}
      {noBranch ? (
        <PortalInlineCard data-testid="portal-no-branch-notice">
          Pick a pharmacy to unlock refills, prescription uploads, OTC shopping and chat with a pharmacist.
        </PortalInlineCard>
      ) : <PortalRefillNudges limit={1} />}

      <section data-testid="portal-quick-actions">
        <div className="grid grid-cols-4 gap-2">
          {QUICK_ACTIONS.map(({ page, search, icon: Icon, label }) => (
            noBranch ? (
              <div key={page} className="portal-quick-action portal-quick-action-disabled" data-testid={`portal-quick-action-${page}-disabled`} aria-disabled="true">
                <Icon className="w-5 h-5" aria-hidden="true" /><span>{label}</span>
              </div>
            ) : (
              <Link key={page} to={`${href(page)}${search || ''}`} className="portal-quick-action" data-testid={`portal-quick-action-${page}`}>
                <Icon className="w-5 h-5" aria-hidden="true" /><span>{label}</span>
              </Link>
            )
          ))}
        </div>
        {noBranch && <p className="text-xs text-slate-500 mt-2" data-testid="portal-quick-actions-disabled-note">
          Choose a pharmacy above to turn these on.
        </p>}
      </section>

      <section data-testid="portal-metric-strip" className="portal-metric-strip">
        <StatTile label="Prescriptions" value={stats.rx} />
        <StatTile label="Active refills" value={stats.refills} />
        <StatTile label="Past orders" value={stats.orders} />
        <StatTile label="Reminders" value={stats.reminders} />
      </section>

      <section className="space-y-3" data-testid="portal-recent-activity">
        <h2 className="text-lg font-semibold text-slate-900">Recent activity</h2>
        {loadingFeed ? <PortalSkeletonRows rows={3} testId="portal-recent-activity-skeleton" /> : (
          activity.length === 0 ? (
            <PortalEmptyState icon={FileText} title="No activity yet"
              description="Your prescriptions, refills and orders will show up here." testId="portal-recent-activity-empty" />
          ) : (
            <div className="space-y-2">
              {activity.map((item) => (
                <Link key={item.key} to={item.to} className="flex items-center gap-3 border rounded-xl p-3 hover:bg-slate-50 card-hover" data-testid={`portal-activity-${item.key}`}>
                  <div className="p-2 rounded-lg bg-slate-100 text-slate-600 shrink-0"><item.icon className="w-5 h-5" aria-hidden="true" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{item.title}</div>
                    <div className="text-xs text-slate-500">{item.subtitle} · {new Date(item.date).toLocaleDateString()}</div>
                  </div>
                </Link>
              ))}
            </div>
          )
        )}
      </section>

      {upcomingReminders.length > 0 && (
        <section className="space-y-3" data-testid="portal-home-reminders">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Bell className="w-4 h-4" aria-hidden="true" />Reminders</h2>
          <div className="space-y-2">
            {upcomingReminders.map((r) => (
              <Link key={r.reminder_id} to={href('reminders')} className="flex items-center justify-between border rounded-xl p-3 hover:bg-slate-50" data-testid={`portal-home-reminder-${r.reminder_id}`}>
                <span className="font-medium truncate">{r.title}</span>
                <span className="text-xs text-slate-500 shrink-0 ml-2">{new Date(r.due_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function PortalInlineCard({ children, ...props }) {
  return (
    <Card className="border-amber-200 bg-amber-50/70" {...props}>
      <CardContent className="p-4 text-sm text-amber-900">{children}</CardContent>
    </Card>
  );
}

function StatTile({ label, value }) {
  return (
    <Card className="border-emerald-100 portal-metric-card card-hover" data-testid={`portal-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="text-2xl font-bold text-slate-900">{value}</div>
        <div className="text-xs text-slate-500 whitespace-nowrap">{label}</div>
      </CardContent>
    </Card>
  );
}
